import { Box, Button, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';
import strings from '../../common/Translation/Translate';
import DrawerMenu from '../../components/DrawerMenu';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axios';
import { URLS } from '../../utils/constants/urls';
type CustomeThemeProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};
const CustomerTheme: FC<CustomeThemeProps> = ({ open, setOpen }) => {
  const defaultTheme = useTheme();
  const screenSizeDownSm = useMediaQuery(defaultTheme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(defaultTheme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(defaultTheme.breakpoints.up('lg'));
  const Navigate = useCustomNavigate();
  const dispatch = useDispatch();
  const [themeRequest, setThemeRequest] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleClose = () => {
    setOpen(false);
  };

  const getInitialResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data }: { status: number; data: any } = await axiosInstance.get('/api/organizations/theme');
      if (status === 200) {
        setThemeRequest(data);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getInitialResponse();
  }, [getInitialResponse]);

  const setForm = useMemo(() => {
    const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        setIsLoading(true);
        const { status, data }: { status: number; data: any } = await axiosInstance.post(
          URLS.setCustomerTheme,
          themeRequest,
        );
        if (status === 200) {
          setIsLoading(false);
          setThemeRequest(data);
          setOpen(false);
          Navigate('/');
        }
      } catch (e) {
        setIsLoading(false);
      }
    };

    const handleOnChange = (e: any, type: string) => {
      const value = e.target.value;
      switch (type) {
        case 'primary':
          setThemeRequest({ ...themeRequest, primary: value });
          break;
        case 'primaryTextColor':
          setThemeRequest({ ...themeRequest, primaryTextColor: value });
          break;
        case 'secondaryMain':
          setThemeRequest({ ...themeRequest, secondaryMain: value });
          break;
        case 'secondaryDark':
          setThemeRequest({ ...themeRequest, secondaryDark: value });
          break;
        case 'secondaryLight':
          setThemeRequest({ ...themeRequest, secondaryLight: value });
          break;
        case 'secondaryTextColor':
          setThemeRequest({ ...themeRequest, secondaryTextColor: value });
          break;
        case 'hoverColor':
          setThemeRequest({ ...themeRequest, hoverColor: value });
          break;
        default:
          break;
      }
    };

    return (
      <Box p={1} width="100%">
        <Grid direction="column" container>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {strings.theme_primary_color} ({themeRequest?.primary})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'primary')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.primary}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {strings.theme_primary_text_color} ({themeRequest?.primaryTextColor})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'primaryTextColor')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.primaryTextColor}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {'Secondary Main'} ({themeRequest?.secondaryMain})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'secondaryMain')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.secondaryMain}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {'Secondary Dark'} ({themeRequest?.secondaryDark})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'secondaryDark')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.secondaryDark}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {'Secondary Light'} ({themeRequest?.secondaryLight})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'secondaryLight')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.secondaryLight}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {'Secondary Text Color'} ({themeRequest?.secondaryTextColor})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'secondaryTextColor')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.secondaryTextColor}
            />
          </Grid>
          <Grid mb={2}>
            <Typography variant="subtitle2" mb={1}>
              {'Hover Color'} ({themeRequest?.hoverColor})
            </Typography>
            <input
              onChange={e => handleOnChange(e, 'hoverColor')}
              type="color"
              id="favcolor"
              name="favcolor"
              value={themeRequest?.hoverColor}
            />
          </Grid>
        </Grid>
        <Divider />
        <Grid container sx={{ my: 3 }}>
          <Button variant="contained" onClick={e => handleSave(e)}>
            {strings.theme_button_text}
          </Button>
        </Grid>
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, setOpen, themeRequest]);

  return (
    <>
      {isLoading && <PageLoader />}
      <DrawerMenu
        open={open}
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        handleClose={handleClose}
        closeButton={true}
        overlapHeader
        title="Customer Theme">
        {setForm}
      </DrawerMenu>
    </>
  );
};
export default CustomerTheme;
